import { type Ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { ONE_MINUTE_MS } from "@/helpers/constants";
import { useNotification } from "@/hooks/notifications";
import applicationsService, {
  type GetApplicationAttributesParams,
} from "@/services/modules/applications";
import useApplicationsStore from "@/stores/applications";

import type { IApplicationAttribute } from "@/models/applications";

export const APPLICATION_QUERY_KEYS = {
  PHONE_NUMBERS: ["options", "checkout"],
  EMAILS: ["deal_emails"],
  APPLICATION_ATTRIBUTES: "application-attributes",
} as const;

export const useBulkToggleOffersSharedMutation = () => {
  const store = useStore();

  type BulkToggleOffersSharedParams = Parameters<
    typeof applicationsService.bulkToggleShowOffers
  >;

  return useMutation({
    mutationFn: (payload: {
      dealId: BulkToggleOffersSharedParams[0];
      data: BulkToggleOffersSharedParams[1];
    }) =>
      applicationsService.bulkToggleShowOffers(payload.dealId, payload.data),
    onSuccess: () => {
      store.dispatch("applications/getOffers");
    },
  });
};

export const useApplicationPhoneNumbersQuery = (appId: Ref<string>) => {
  const applicationsStore = useApplicationsStore();

  return useQuery({
    queryKey: [APPLICATION_QUERY_KEYS.PHONE_NUMBERS, appId],
    queryFn: async () => {
      const data = await applicationsService.getApplicationPhoneNumbers(
        appId.value
      );
      applicationsStore.setPhoneNumbers(data);
      return data;
    },
    staleTime: ONE_MINUTE_MS,
    enabled: computed(() => !!appId.value),
  });
};

export const useApplicationAttributesQuery = (
  appId: Ref<string>,
  params: Ref<GetApplicationAttributesParams>
) => {
  return useQuery({
    queryKey: computed(() => [
      APPLICATION_QUERY_KEYS.APPLICATION_ATTRIBUTES,
      appId.value,
      params.value,
    ]),
    queryFn: () =>
      applicationsService.getApplicationAttributes(appId.value, params.value),
    staleTime: ONE_MINUTE_MS,
    enabled: computed(() => !!appId.value),
    placeholderData: (previousData) => previousData,
  });
};

export const useUpdateApplicationAttributeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IApplicationAttribute) =>
      applicationsService.updateApplicationAttributeValue(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          APPLICATION_QUERY_KEYS.APPLICATION_ATTRIBUTES,
          variables.application_id,
        ],
      });
    },
  });
};

export const useResubmitPlacementMutation = () => {
  const { t } = useI18n();
  const { showMessage } = useNotification();

  return useMutation({
    mutationFn: (payload: {
      applicationId: string;
      placementId: string;
      productName: string;
      funderName: string;
    }) =>
      applicationsService.resubmitPlacement({
        applicationId: payload.applicationId,
        placementId: payload.placementId,
      }),
    onSuccess: (_, variables) => {
      showMessage(
        t("DEALS.PLACEMENT.RESUBMIT_SUCCESS", {
          product: variables.productName,
          funder: variables.funderName,
        }),
        "success"
      );
    },
    onError: (_, variables) => {
      showMessage(
        t("DEALS.PLACEMENT.RESUBMIT_ERROR", {
          product: variables.productName,
          funder: variables.funderName,
        }),
        "error"
      );
    },
  });
};

export const useApplicationEmailsQuery = ({
  appId,
  enabled,
}: {
  appId: Ref<string>;
  enabled: Ref<boolean>;
}) => {
  return useQuery({
    queryKey: [APPLICATION_QUERY_KEYS.EMAILS, appId],
    queryFn: () => applicationsService.getApplicationEmails(appId.value),
    staleTime: ONE_MINUTE_MS,
    enabled: computed(() => !!enabled.value),
  });
};
